import { useContext, useEffect, useState } from 'react'

import { Props as ArticleCardProps } from '@hhgtech/hhg-components/build/components/molecules/articleCard/_deprecated_index'
import { useSSOV2Store } from '@hhgtech/hhg-components/ssoV2'
import innertext from 'innertext'
import Link from 'next/link'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { CardArticleAuthorName } from 'components/atoms/CardArticleV2/CardArticleAuthor'
import { getArticleCardDisplayAuthor } from 'components/pages/article/utils'
import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { IS_SSOV2_ENABLED, SSO_URL } from 'interfaces/constants'
import { Article } from 'interfaces/types'
import { AppContext } from 'state/appContext'

// import content from '*.svg'
import { getAuthorFromArticleApi } from 'utils/author'

import { actionTypes } from '../../../state/actionTypes'
import { ExpertArticleCard } from '../expertArticleCard'

type Props = Omit<ArticleCardProps, 'avatarImg' | 'authorName'> & {
  article: Article
  avatarImg?: JSX.Element
  authorName?: string
  authorLink?: string
  title: string
  text: string
  avatarImgRenderer?: (avatar: string, name: string) => JSX.Element
}

export const ExpertArticleCardWrapper = ({
  article,
  avatarImg,
  avatarImgRenderer,
  authorName,
  authorLink,
  title,
  text,
  ...rest
}: Props) => {
  const { state, dispatch } = useContext(AppContext)
  const { userInfo } = state || {}
  const { bookmarks = [] } = userInfo || {}
  const [isBookmarked, setIsBookmarked] = useState(
    bookmarks.indexOf(article.id) !== -1,
  )
  const [isBookmarkDisplayed, setIsBookmarkDisplayed] = useState(false)
  const { locale } = useRouterExtend()
  const author = getArticleCardDisplayAuthor(article)
  const authorV2 = getAuthorFromArticleApi({
    article,
    locale,
    version: 'short',
  })

  const bookmarkArticle = (id: string | number) => {
    if (userInfo?.id) {
      setIsBookmarked((isBookmarked) => !isBookmarked)
      if (isBookmarked) {
        ;(async () => {
          const { _status } = await callApiNew(
            PATHS.SSO.DELETE_BOOKMARK,
            {
              method: 'DELETE',
              data: {
                host_name: domainLocales[locale],
                post_ids: [id],
              },
            },
            'sso',
          )
          if (_status === 1) {
            dispatch({
              type: actionTypes.REMOVE_BOOKMARKS,
              payload: {
                bookmarks: [id],
                bookmarkId: id,
              },
            })
          }
        })()
      } else {
        ;(async () => {
          const { _status, _data } = await callApiNew(
            PATHS.SSO.CREATE_BOOKMARK,
            {
              method: 'POST',
              data: {
                host_name: domainLocales[locale],
                post_ids: [id],
              },
            },
            'sso',
          )
          if (_status === 1) {
            dispatch({
              type: actionTypes.ADD_BOOKMARKS,
              payload: {
                bookmarks: [id],
                bookmarkId: id,
                isFirstTimeBookmark: _data.first_time_bookmark,
              },
            })
          }
        })()
      }
    } else {
      const currentLocation = window.location.href
      const returnUrlEncode = encodeURIComponent(
        `${currentLocation}${
          currentLocation.includes('?') ? '&' : '?'
        }source=bookmark,${id}`,
      )
      if (IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          source: `bookmark,${id}`,
        })
      } else {
        window.location.href = SSO_URL
          ? `${SSO_URL}?returnUrl=${returnUrlEncode}`
          : currentLocation
      }
    }
  }

  useEffect(() => {
    setIsBookmarkDisplayed(true)
  }, [])

  useEffect(() => {
    setIsBookmarked(bookmarks.indexOf(article.id) !== -1)
  }, [bookmarks.length])

  return (
    <ExpertArticleCard
      dataEventCategoryForBookmark="Bookmark"
      dataEventActionForBookmark="Click"
      dataEventLabelForBookmark={`https://${domainLocales[locale]}${article.permalinkDecode}`}
      {...rest}
      title={title ? innertext(title) : ''}
      text={text ? innertext(text) : ''}
      avatarImg={
        <Link href={authorLink || author.permalink} prefetch={false}>
          {avatarImg ||
            (avatarImgRenderer &&
              avatarImgRenderer(author.avatar, author.name)) || (
              <img loading="lazy" src={author.avatar} alt={author.name} />
            )}
        </Link>
      }
      authorName={
        <>
          <CardArticleAuthorName {...authorV2} />
        </>
      }
      authorLink={authorLink || author.permalink}
      isBookmarkDisplayed={isBookmarkDisplayed}
      isBookmark={isBookmarked}
      onBookmark={() => bookmarkArticle(article.id)}
    />
  )
}
